import { sql } from '@w/db'
import { cardKindCodes as k } from '../server/db/flashcards.schema'

/**
 * Write-path card derivation (db-crdt-sync requirements :47, :49; genericCards-spaces.decisions.md 2026-10-25 option A).
 * The commit that carries a mutation derives its author's genericCards rows from that mutation's applied rows, in the
 * same transaction. Receivers apply the same CDC to their local projection. One set of statements serves both write
 * paths (writePageEdit and authoredSourceBatch), reading the page's applied rows in the open transaction (F13).
 */
export type DerivationArgs = {
  pageId: string
  /** The author: the commit's session user. Only this user's rows are written. */
  userId: string
  /** The page row's updatedAt in this transaction, epoch ms (the operation time, spec INV-L3). */
  updatedAt: number
}

/** The page's applied rows in the open transaction (F13 `crdt_tx_applied`, cleared at COMMIT/ROLLBACK, never recorded on replication apply). */
export const txApplied = (pageId: string) => sql`crdt_tx_applied('pages',(SELECT rowid FROM pages WHERE id=${pageId}))`

type Source = { kind: 'applied' } | { kind: 'page' } | {kind: 'received'; events: string}

/** The mutation's applied rows; for a created or restored page, every question posting of the page reads as inserted. */
function appliedFrom(source: Source, a: DerivationArgs) {

  if(source.kind === 'received') return sql`(SELECT json_extract(value,'$.kind') AS kind,json_extract(value,'$.node') AS node,json_extract(value,'$.key') AS key,json_extract(value,'$.oldValue') AS old_value,json_extract(value,'$.value') AS value,json_extract(value,'$.qid') AS qid FROM json_each(${source.events}))`
  return source.kind === 'applied'
    ? sql`(SELECT kind,node,key,old_value,value,qid FROM ${txApplied(a.pageId)})`
    : sql`(SELECT 'insert' AS kind,s.node AS node,NULL AS key,NULL AS old_value,NULL AS value,s.fields->>'qid' AS qid
        FROM crdt_attr_lookup('idx_pages_crdt_attr',NULL,(SELECT rowid FROM pages WHERE id=${a.pageId}),'qid') s)`
}

/** Insert/remove derive rows; position changes relocate them without copying block defaults again. */
function affected(source: Source, a: DerivationArgs) {
  return sql`SELECT e.qid,MAX(e.kind='insert') AS inserted
    FROM ${appliedFrom(source, a)} e WHERE (e.kind IN ('insert','remove','move') OR (e.kind='set' AND e.key IN ('fractionalIndex','importance','difficulty','subtype','tta'))) AND e.qid IS NOT NULL GROUP BY e.qid`
}

export const questionTypes = sql.raw`('question','cloze','rule')`

/**
 * A question's lexicon card kind (lexicon-cards.ts entryCardKinds) from the engine index, as SQL text over the given
 * qid and page id expressions: the entry node that names the qid (a sense's senseRecallQuestionId or
 * formRecallQuestionId; a paradigm form carries `cell`), else the lexeme's readAloud/dictation column, else NULL (a note
 * question). The index keys include both entry keys (202610210001_card_derivation.sql). One lookup by qid on one page,
 * never a page decode (em requirements :41). Shared by the derivation, the page triggers and the read-time union.
 */
export function questionKindSql(qid: string, pageId: string) {
  return `(SELECT COALESCE((SELECT CASE WHEN (e.fields->>'senseRecallQuestionId')=${qid} THEN ${k.senseRecall} WHEN (e.fields->>'cell') IS NOT NULL THEN ${k.formRecallParadigmCell} ELSE ${k.formRecall} END
    FROM crdt_attr_lookup('idx_pages_crdt_attr',${qid},kp.rowid) e WHERE (e.fields->>'senseRecallQuestionId')=${qid} OR (e.fields->>'formRecallQuestionId')=${qid} LIMIT 1),
    CASE ${qid} WHEN kp.readAloudQuestionId THEN ${k.readAloud} WHEN kp.dictationQuestionId THEN ${k.dictation} END) FROM pages kp WHERE kp.id=${pageId})`
}
const live = (qid: ReturnType<typeof sql.raw>) => sql`EXISTS(SELECT 1 FROM crdt_attr_lookup('idx_pages_crdt_attr',${qid}) s
  JOIN pages p ON p.rowid=s.pageRowid WHERE p.deletedAt IS NULL AND (s.fields->>'type') IN ${questionTypes} AND (s.fields->>'qid')=${qid})`

/** Keep the live generic source when a duplicate qid is inserted; otherwise choose page id and document order. */
function candidates(source: Source, a: DerivationArgs) {
  return sql`SELECT qid,node,pageId,sourceUpdatedAt,src,inserted,${sql.raw(questionKindSql('qid', 'pageId'))} AS kind FROM (
      SELECT a.qid,s.node,p.id AS pageId,p.updatedAt AS sourceUpdatedAt,s.fields AS src,a.inserted,
        ROW_NUMBER() OVER(PARTITION BY a.qid ORDER BY CASE WHEN p.id=(SELECT g.pageId FROM genericCards g WHERE g.qid=a.qid AND g.deletedAt IS NULL) THEN 0 ELSE 1 END,p.id,s.docOrder) AS rank
      FROM (${affected(source, a)}) a CROSS JOIN crdt_attr_lookup('idx_pages_crdt_attr',a.qid) s
      JOIN pages p ON p.rowid=s.pageRowid
      WHERE (p.deletedAt IS NULL ${source.kind === 'received' ? sql`OR p.id=${a.pageId}` : sql``}) AND (s.fields->>'type') IN ${questionTypes} AND (s.fields->>'qid')=a.qid
    ) WHERE rank=1`
}

export const importance = sql.raw`CASE (src->>'importance') WHEN 'low' THEN 0 WHEN 'high' THEN 2 ELSE 1 END`
export const difficulty = sql.raw`CASE (src->>'difficulty') WHEN 'low' THEN 0 WHEN 'high' THEN 2 ELSE 1 END`
export const promptType = sql.raw`CASE (src->>'type') WHEN 'cloze' THEN 4 WHEN 'rule' THEN 4 ELSE CASE (src->>'subtype') WHEN 'la' THEN 1 WHEN 'sc' THEN 2 WHEN 'mc' THEN 3 WHEN 'sr' THEN 5 ELSE 0 END END`
export const tta = sql.raw`COALESCE((src->>'tta'),30)`

/**
 * The remover's commit keeps each removed question part's content (db-crdt-sync requirements :49): a freeze folds the
 * CRDT's tombstone content away (:26) while deleted review, Clone and Restore still read it (spaces spec INV-L7, :145).
 * A part is kept only while no part of its qid is live on the page; a live insert of the qid drops the page's kept parts.
 */
export function retainedPartStatements(a: DerivationArgs) {
  const applied = txApplied(a.pageId)
  return [
    sql`DELETE FROM retainedParts WHERE pageId=${a.pageId} AND qid IN (SELECT qid FROM ${applied} WHERE kind='insert' AND qid IS NOT NULL)`,
    sql`INSERT OR REPLACE INTO retainedParts(pageId,qid,node,content,removedBy,removedAt)
      SELECT ${a.pageId},r.qid,r.node,r.content,${a.userId},${a.updatedAt} FROM ${applied} r
      WHERE r.kind='remove' AND r.qid IS NOT NULL AND r.content IS NOT NULL AND (r.fields->>'type') IN ${questionTypes}
        AND NOT EXISTS(SELECT 1 FROM crdt_attr_lookup('idx_pages_crdt_attr',r.qid,(SELECT rowid FROM pages WHERE id=${a.pageId})) live
          WHERE (live.fields->>'qid')=r.qid AND (live.fields->>'type') IN ${questionTypes})`,
  ]
}

/** Statements deriving the author's cards from one mutation (or from a created/restored page's postings). */
export function cardDerivationStatements(source: Source, a: DerivationArgs) {
  const personalUser = source.kind === 'received' ? '' : a.userId
  const writes = [
    // Preserve the last projected source location when an own removal retains a personal card.
    sql`UPDATE flashcards SET (node,fractionalIndex)=(g.node,g.fractionalIndex)
      FROM genericCards g WHERE flashcards.userId=${personalUser} AND flashcards.qid=g.qid
        AND flashcards.pageId=${a.pageId} AND flashcards.qid IN (SELECT qid FROM (${affected(source, a)}))
        AND NOT ${live(sql.raw`flashcards.qid`)}`,

    // An authored removal is also this learner's action on an existing personal card.
    // Receive uses no personal principal and therefore cannot update a personal card.
    sql`UPDATE flashcards SET deletedAt=${a.updatedAt},deletedBy=${a.userId}
      WHERE userId=${personalUser} AND pageId=${a.pageId}
        AND qid IN (SELECT qid FROM (${affected(source, a)}))
        AND NOT ${live(sql.raw`flashcards.qid`)} AND deletedAt IS NULL`,
    sql`UPDATE flashcards SET deletedAt=NULL,deletedBy=NULL
      WHERE userId=${personalUser} AND deletedBy IS NOT NULL
        AND qid IN (SELECT qid FROM (${affected(source, a)}) WHERE inserted=1)
        AND ${live(sql.raw`flashcards.qid`)}`,
    // Last live part gone: the author's own card is soft-deleted at the operation time. Other users' cards are never written.
    sql`UPDATE genericCards SET deletedAt=${a.updatedAt},deletedBy=${a.userId},updatedAt=${a.updatedAt}
      WHERE rowid IN (SELECT c.rowid FROM (${affected(source, a)}) a CROSS JOIN genericCards c INDEXED BY sqlite_autoindex_genericCards_1
        ON c.qid=CAST(a.qid AS TEXT) WHERE c.pageId=${a.pageId})
        AND NOT ${live(sql.raw`genericCards.qid`)}`,
    sql`INSERT INTO genericCards(qid,pageId,node,fractionalIndex,importance,difficulty,promptType,ttaSeconds,cardKind,updatedAt)
      SELECT qid,pageId,node,COALESCE((src->>'fractionalIndex'),'a0'),${importance},${difficulty},${promptType},${tta},kind,sourceUpdatedAt
      FROM (${candidates(source, a)}) v
      WHERE v.inserted=1 AND NOT EXISTS(SELECT 1 FROM genericCards z INDEXED BY sqlite_autoindex_genericCards_1 WHERE z.qid=CAST(v.qid AS TEXT))
      ON CONFLICT(qid) DO NOTHING`,
    // Insertion can relocate a restored/moved card; its independent values stay unchanged.
    sql`UPDATE genericCards SET (pageId,node,fractionalIndex,updatedAt)=(v.pageId,v.node,v.f,v.sourceUpdatedAt)
      FROM (SELECT x.rowid AS id,a.pageId,a.node,a.sourceUpdatedAt,COALESCE((src->>'fractionalIndex'),'a0') AS f
        FROM (${candidates(source, a)}) a CROSS JOIN genericCards x INDEXED BY sqlite_autoindex_genericCards_1
          ON x.qid=CAST(a.qid AS TEXT)) v
      WHERE genericCards.rowid=v.id AND (genericCards.pageId,genericCards.node,genericCards.fractionalIndex,genericCards.updatedAt) IS NOT (v.pageId,v.node,v.f,v.sourceUpdatedAt)`,
    // Generic defaults describe the current shared question; personal copies are independent.
    sql`UPDATE genericCards SET (importance,difficulty,promptType,ttaSeconds,cardKind)=(v.i,v.d,v.p,v.t,v.kind)
      FROM (SELECT qid,${importance} AS i,${difficulty} AS d,${promptType} AS p,${tta} AS t,kind FROM (${candidates(source,a)})) v
      WHERE genericCards.qid=v.qid AND (genericCards.importance,genericCards.difficulty,genericCards.promptType,genericCards.ttaSeconds,genericCards.cardKind) IS NOT (v.i,v.d,v.p,v.t,v.kind)`,
    // An author's structural edit relocates its existing personal card, without changing learned values.
    sql`UPDATE flashcards SET (pageId,node,fractionalIndex)=(g.pageId,g.node,g.fractionalIndex)
      FROM genericCards g WHERE flashcards.userId=${personalUser} AND flashcards.qid=g.qid
        AND flashcards.qid IN (SELECT qid FROM (${affected(source, a)}))
        AND (flashcards.pageId=${a.pageId} OR flashcards.deletedAt IS NOT NULL
          OR NOT EXISTS(SELECT 1 FROM pages old,crdt_attr_lookup('idx_pages_crdt_attr',flashcards.qid,old.rowid) part
            WHERE old.id=flashcards.pageId AND old.deletedAt IS NULL AND part.fields->>'qid'=flashcards.qid))
        AND (flashcards.pageId,flashcards.node,flashcards.fractionalIndex) IS NOT (g.pageId,g.node,g.fractionalIndex)`,
    // Re-inserted part (undo, restore): the author's deleted card is live again.
    sql`UPDATE genericCards SET deletedAt=NULL,deletedBy=NULL
      WHERE rowid IN (SELECT c.rowid FROM (${affected(source, a)}) a CROSS JOIN genericCards c INDEXED BY sqlite_autoindex_genericCards_1
          ON c.qid=CAST(a.qid AS TEXT)
        WHERE a.inserted=1 AND ${live(sql.raw`a.qid`)})
        AND (deletedAt IS NOT NULL OR deletedBy IS NOT NULL)`,
    sql`DELETE FROM genericCards WHERE deletedAt IS NOT NULL
      AND pageId=${a.pageId} AND NOT EXISTS (SELECT 1 FROM flashcards p WHERE p.qid=genericCards.qid)`,
  ]
  return writes
}

/** Page soft deletion by its author: the author's cards of the page are deleted at the operation time. */
export function pageDeletionStatements(a: DerivationArgs) {
  // The card's deletion time is the page's deletedAt (as derive_owner_cards_deleted wrote it, init.sql), so a replayed
  // delete commit, which advances updatedAt but not deletedAt, writes nothing.
  const deletedAt = sql`(SELECT p.deletedAt FROM pages p WHERE p.id=${a.pageId})`
  return [sql`UPDATE flashcards SET deletedBy=${a.userId},deletedAt=${deletedAt}
    WHERE userId=${a.userId} AND pageId=${a.pageId} AND deletedAt IS NULL`,
    sql`UPDATE genericCards SET deletedBy=${a.userId},deletedAt=${deletedAt}
    WHERE pageId=${a.pageId} AND (deletedBy IS NOT ${a.userId} OR deletedAt IS NOT ${deletedAt})`]
}

/** Receive CDC is a batch relation: parse and join it in SQL once per table, never once per page. */
export function receivedCardBatchStatements(questions: string) {
  const affected = sql`SELECT e.value->>'pageId' AS pageId,e.value->>'qid' AS qid,
      MAX(e.value->>'inserted') AS inserted,e.value->>'updatedAt' AS updatedAt,e.value->>'editedBy' AS editedBy
    FROM json_each(${questions}) e GROUP BY e.value->>'pageId',e.value->>'qid'`
  return [
    sql`WITH affected AS MATERIALIZED (${affected}), candidates AS MATERIALIZED (
        SELECT a.qid,p.id AS pageId,s.node,s.fields AS src,p.updatedAt AS sourceUpdatedAt,p.deletedAt AS sourceDeletedAt,
          a.inserted,g.deletedAt AS previousDeletedAt,g.deletedBy AS previousDeletedBy,
          ROW_NUMBER() OVER(PARTITION BY a.qid ORDER BY CASE WHEN p.id=g.pageId AND g.deletedAt IS NULL THEN 0 ELSE 1 END,p.id,s.docOrder) AS rank
        FROM (SELECT qid,MAX(inserted) AS inserted FROM affected GROUP BY qid) a
        CROSS JOIN crdt_attr_lookup('idx_pages_crdt_attr',a.qid) s
        JOIN pages p ON p.rowid=s.pageRowid LEFT JOIN genericCards g ON g.qid=CAST(a.qid AS TEXT)
        WHERE (p.deletedAt IS NULL OR p.id IN (SELECT pageId FROM affected WHERE qid=a.qid))
          AND s.fields->>'type' IN ${questionTypes} AND s.fields->>'qid'=a.qid
          AND (a.inserted=1 OR g.qid IS NOT NULL)
      )
      INSERT INTO genericCards(qid,pageId,node,fractionalIndex,importance,difficulty,promptType,ttaSeconds,cardKind,updatedAt,deletedAt,deletedBy)
      SELECT qid,pageId,node,COALESCE(src->>'fractionalIndex','a0'),${importance},${difficulty},${promptType},${tta},
        ${sql.raw(questionKindSql('qid','pageId'))},sourceUpdatedAt,
        CASE WHEN inserted=1 AND sourceDeletedAt IS NULL THEN NULL ELSE previousDeletedAt END,
        CASE WHEN inserted=1 AND sourceDeletedAt IS NULL THEN NULL ELSE previousDeletedBy END
      FROM candidates WHERE rank=1
      UNION ALL
      SELECT g.qid,g.pageId,g.node,g.fractionalIndex,g.importance,g.difficulty,g.promptType,g.ttaSeconds,g.cardKind,
        a.updatedAt,a.updatedAt,a.editedBy
      FROM affected a JOIN genericCards g ON g.qid=CAST(a.qid AS TEXT) AND g.pageId=a.pageId
      WHERE NOT ${live(sql.raw`g.qid`)}
        AND NOT EXISTS (SELECT 1 FROM candidates c WHERE c.qid=a.qid)
      ON CONFLICT(qid) DO UPDATE SET pageId=excluded.pageId,node=excluded.node,fractionalIndex=excluded.fractionalIndex,
        importance=excluded.importance,difficulty=excluded.difficulty,promptType=excluded.promptType,
        ttaSeconds=excluded.ttaSeconds,cardKind=excluded.cardKind,updatedAt=excluded.updatedAt,
        deletedAt=excluded.deletedAt,deletedBy=excluded.deletedBy`,
  ]
}
